import { Trans, useLingui } from '@lingui/react/macro'
import {
  Box,
  Button,
  Card,
  Loader,
  Menu,
  Stack,
  Tabs,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { AppPage } from '@markku/components'
import { groupBy, isCompanyType } from '@markku/utils'
import {
  IconBuilding,
  IconCalendar,
  IconChartBar,
  IconChevronDown,
  IconChevronRight,
  IconHome,
  IconPlus,
} from '@tabler/icons-react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { AddPropertyModal } from '../../components/profile/AddPropertyModal'
import { usePropertyCreation } from '../../lib/hooks/usePropertyCreation'
import { propertyHooks } from '../../lib/trpcHooks'
import { Summary } from '../../modules/maintenance/Summary'
import { YearCalendarCarousel } from '../../modules/maintenance/YearCalendarCarousel'

function MaintenancePage() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const search = useSearch({ from: '/_app/maintenance' })
  const { handlePropertyCreated, WelcomeModal } = usePropertyCreation()
  const [
    addFirstPropertyOpened,
    { open: openAddFirstProperty, close: closeAddFirstProperty },
  ] = useDisclosure(false)

  const handlePropertyCreatedAndCloseModal = (
    propertyId: number,
    propertyAddress: string,
  ) => {
    closeAddFirstProperty()
    handlePropertyCreated(propertyId, propertyAddress)
  }

  // Fetch user's properties
  const { data: propertiesData, isLoading } = propertyHooks.useList()
  const properties = propertiesData?.data

  // Get active tab and selected propertyId from URL search params
  const activeTab = (search as any).tab || 'yearCalendar'
  const selectedPropertyId = (search as any).propertyId

  // Default to first property if none selected
  const propertyId =
    selectedPropertyId && properties?.length
      ? Number(selectedPropertyId)
      : properties?.[0]?.id

  // Handle tab change by updating URL with replace to avoid adding history entries
  const handleTabChange = (value: string | null) => {
    if (value) {
      navigate({
        search: (prev: any) => ({ ...prev, tab: value }),
        replace: true,
      } as any)
    }
  }

  // Handle property change
  const handlePropertyChange = (value: string | null) => {
    if (value) {
      navigate({
        search: (prev: any) => ({ ...prev, propertyId: Number(value) }),
        replace: true,
      } as any)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <AppPage
        title={t`Maintenance log`}
        subtitle={t`Tracking and summary`}
        backTarget="/home"
      >
        <Stack align="center" justify="center" h={200}>
          <Loader size="lg" />
        </Stack>
      </AppPage>
    )
  }

  // No properties found
  if (!properties?.length) {
    return (
      <AppPage
        title={t`Maintenance log`}
        subtitle={t`Tracking and summary`}
        backTarget="/home"
      >
        <Card
          radius="md"
          withBorder
          onClick={openAddFirstProperty}
          style={{
            cursor: 'pointer',
            borderColor: 'var(--mantine-color-brandOrange-2)',
            transition: 'all 0.2s',
          }}
          styles={{
            root: {
              '&:hover': {
                borderColor: 'var(--mantine-color-brandOrange-4)',
                transform: 'translateY(-2px)',
              },
            },
          }}
        >
          <Stack gap="md" align="center" py="xl">
            <Box
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--mantine-color-brandOrange-0)',
              }}
            >
              <IconHome size={30} color="var(--mantine-color-brandOrange-6)" />
            </Box>
            <Stack gap={4} align="center">
              <Text fw={600} size="lg">
                <Trans>Add your first property</Trans>
              </Text>
              <Text c="dimmed" ta="center">
                <Trans>
                  Start by adding a property for the maintenance log
                </Trans>
              </Text>
            </Stack>
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              color="brandOrange"
            >
              <Trans>Add property</Trans>
            </Button>
          </Stack>
        </Card>

        <AddPropertyModal
          opened={addFirstPropertyOpened}
          onClose={closeAddFirstProperty}
          onPropertyCreated={handlePropertyCreatedAndCloseModal}
        />

        {WelcomeModal}
      </AppPage>
    )
  }

  // Get selected property
  const selectedProperty = properties.find((p) => p.id === propertyId)
  const propertyAddress = selectedProperty?.address
    ? `${selectedProperty.address.street}, ${selectedProperty.address.city}`
    : t`Property`

  // Property selector for multi-property users - grouped by customer
  const propertyOptions = properties.map((p) => ({
    value: p.id.toString(),
    label: p.address?.street || t`Property ${p.id}`,
    street: p.address?.street || t`Property ${p.id}`,
    city: p.address?.city,
    customerId: p.customer?.id ?? null,
    customerName: p.customer?.name || t`No customer`,
    customerType: p.customer?.type ?? null,
  }))

  // Group properties by customer id (ids can't collide like names can, and we need the
  // id to link business groups straight into the company portal)
  const propertyGroups = Array.from(
    groupBy(propertyOptions, (p) => p.customerId ?? 'none').values(),
  ).map((props) => ({
    customerId: props[0].customerId,
    customerName: props[0].customerName,
    customerType: props[0].customerType,
    properties: props,
  }))

  const selectedOption = propertyOptions.find(
    (o) => o.value === propertyId?.toString(),
  )

  const propertySelector =
    properties.length > 1 ? (
      <Menu position="bottom-end" width={250}>
        <Menu.Target>
          <UnstyledButton
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Stack gap={2} align="flex-end" style={{ flex: 1 }}>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {selectedOption?.street || t`Property`}
                </Text>
              </Box>
              {selectedOption?.city && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {selectedOption.city}
                </Text>
              )}
            </Stack>
            <IconChevronDown
              size={12}
              color="var(--mantine-color-gray-6)"
              style={{ flexShrink: 0 }}
            />
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          {propertyGroups.map((group, groupIndex) => {
            // Business properties live in the company portal — selecting one jumps there
            // instead of staying on the resident maintenance view.
            const isBusiness =
              group.customerId && isCompanyType(group.customerType)
            return (
              <div key={group.customerId ?? 'none'}>
                {groupIndex > 0 && <Menu.Divider />}
                <Menu.Label>{group.customerName}</Menu.Label>
                {group.properties.map((opt) => (
                  <Menu.Item
                    key={opt.value}
                    rightSection={
                      isBusiness ? <IconChevronRight size={14} /> : undefined
                    }
                    onClick={() =>
                      isBusiness
                        ? navigate({
                            to: `/company-portal/${group.customerId}/properties/${opt.value}`,
                          })
                        : handlePropertyChange(opt.value)
                    }
                  >
                    <Stack gap={0}>
                      <Text size="sm">{opt.street}</Text>
                      {opt.city && (
                        <Text size="xs" c="dimmed">
                          {opt.city}
                        </Text>
                      )}
                    </Stack>
                  </Menu.Item>
                ))}
              </div>
            )
          })}
        </Menu.Dropdown>
      </Menu>
    ) : undefined

  return (
    <>
      <AppPage
        title={t`Maintenance log`}
        subtitle={properties.length === 1 ? propertyAddress : undefined}
        backTarget="/home"
        rightSection={propertySelector}
      >
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List grow>
            <Tabs.Tab
              value="yearCalendar"
              leftSection={<IconCalendar size={16} />}
            >
              <Trans>Year calendar</Trans>
            </Tabs.Tab>
            <Tabs.Tab value="summary" leftSection={<IconChartBar size={16} />}>
              <Trans>Property</Trans>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="yearCalendar">
            <Box mt="md">
              {propertyId && <YearCalendarCarousel propertyId={propertyId} />}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="summary">
            <Box mt="md">
              {propertyId && <Summary propertyId={propertyId} />}
            </Box>
          </Tabs.Panel>
        </Tabs>
      </AppPage>
    </>
  )
}

export const Route = createFileRoute('/_app/maintenance')({
  component: MaintenancePage,
})
